package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.model.DaySpecials
import com.evoapps.evoshift.domain.model.LocalEvent
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.usecase.specialdays.DeleteLocalEventUseCase
import com.evoapps.evoshift.domain.usecase.specialdays.LocalEventProblem
import com.evoapps.evoshift.domain.usecase.specialdays.ObserveSpecialDaysUseCase
import com.evoapps.evoshift.domain.usecase.specialdays.SaveLocalEventUseCase
import com.evoapps.evoshift.saves.PendingSaves
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate

/** SH-227: one of the person's other workplaces a new event may also go to. */
data class WorkplaceChoice(val id: String, val name: String)

/** SH-227: the event dialog: a new event on [date] ([editing] null) or [editing] an event. */
data class LocalEventDialogState(
    val date: LocalDate,
    val editing: LocalEvent? = null,
    /** A new event's dialog: the day's events typed in already, to open one instead. */
    val dayEvents: List<LocalEvent> = emptyList(),
    /** A new event's dialog: the person's other workplaces it may also go to ("Also at"). */
    val otherWorkplaces: List<WorkplaceChoice> = emptyList()
)

/** SH-227: a screen's special days, the events behind them and the event dialog. */
data class SpecialDaysState(
    val days: Map<LocalDate, DaySpecials> = emptyMap(),
    val events: List<LocalEvent> = emptyList(),
    /** Owners and managers add, change and remove events (the rules allow only them). */
    val canEdit: Boolean = false,
    val dialog: LocalEventDialogState? = null
) {
    /** The events typed in on [date], as stored (their whole dates, the yearly flag). */
    fun eventsOn(date: LocalDate): List<LocalEvent> =
        days[date]?.events.orEmpty().mapNotNull { day -> events.firstOrNull { it.id == day.eventId } }
}

/**
 * SH-227: the special days of the period a view model shows (the roster's month), live, and the
 * small event dialog: opened from a day's menu or by holding a day, for owners and managers.
 * Saving closes the dialog at once (the listener shows the event from the device's cache); the
 * write goes on through [saves], which reports a failure with Retry.
 */
class SpecialDaysController(
    private val scope: CoroutineScope,
    private val observe: ObserveSpecialDaysUseCase?,
    private val save: SaveLocalEventUseCase?,
    private val delete: DeleteLocalEventUseCase?,
    private val saves: PendingSaves
) {
    private val _state = MutableStateFlow(SpecialDaysState())
    val state: StateFlow<SpecialDaysState> = _state.asStateFlow()

    private var job: Job? = null
    private var workplaceId: String? = null
    private var userId: String? = null
    private var others: List<WorkplaceChoice> = emptyList()
    private var newEvents = 0

    /** Follows [workplace]'s special days from [from] to [to]; [canEdit]: the person may change events. */
    fun follow(workplace: Workplace, from: LocalDate, to: LocalDate, user: User?, canEdit: Boolean) {
        val observe = observe ?: return
        workplaceId = workplace.id
        userId = user?.uid
        others = user?.engagements.orEmpty().values
            .filter { it.isActive && it.permission != UserPermission.EMPLOYEE && it.workplaceId != workplace.id }
            .map { WorkplaceChoice(it.workplaceId, it.cachedWorkplaceName?.takeIf { n -> n.isNotBlank() && n != "TBD" } ?: it.workplaceId) }
            .sortedBy { it.name.lowercase() }
        _state.update { it.copy(canEdit = canEdit && save != null && user != null) }
        job?.cancel()
        job = scope.launch {
            observe(workplace, from, to).collect { snapshot ->
                _state.update { it.copy(days = snapshot.days, events = snapshot.events) }
            }
        }
    }

    /** "Add event…" on [date], or holding the day: a new event, the day's events listed to open. */
    fun open(date: LocalDate) {
        if (!_state.value.canEdit) return
        _state.update { it.copy(dialog = LocalEventDialogState(date, dayEvents = it.eventsOn(date), otherWorkplaces = others)) }
    }

    /** "Edit "Open'er"…": the event [eventId] in the dialog. */
    fun edit(eventId: String, date: LocalDate? = null) {
        if (!_state.value.canEdit) return
        val event = _state.value.events.firstOrNull { it.id == eventId } ?: return
        _state.update { it.copy(dialog = LocalEventDialogState(date ?: event.from, editing = event)) }
    }

    fun dismiss() = _state.update { it.copy(dialog = null) }

    /**
     * Saves the dialog's event (new, or the one being edited) and closes it; returns what is wrong
     * instead when it cannot be saved (the dialog stays). [alsoIn]: a new event's other workplaces.
     */
    fun submit(name: String, from: LocalDate, to: LocalDate, yearly: Boolean, alsoIn: Set<String> = emptySet()): LocalEventProblem? {
        SaveLocalEventUseCase.problemOf(name, from, to)?.let { return it }
        val dialog = _state.value.dialog ?: return null
        val save = save ?: return null
        val workplace = workplaceId ?: return null
        val user = userId ?: return null
        val allowed = others.map { it.id }.toSet()
        _state.update { it.copy(dialog = null) }
        saves.launch(saveKey(dialog.editing?.id ?: "new-${newEvents++}")) {
            save(workplace, dialog.editing, name, from, to, yearly, user, alsoIn = alsoIn.filter { it in allowed })
        }
        return null
    }

    /** Removes the event being edited and closes the dialog. */
    fun deleteEditing() {
        val event = _state.value.dialog?.editing ?: return
        val delete = delete ?: return
        val workplace = workplaceId ?: return
        _state.update { it.copy(dialog = null) }
        saves.launch(saveKey(event.id)) { delete(workplace, event.id) }
    }

    private fun saveKey(id: String) = "local-event-$id"
}
